// Pure-logic helpers for the Toast component.
//
// Kept in a JSX-free .ts file so the node-pool test suite can import
// them without needing a jsdom environment or testing-library wired
// in. Toast.tsx imports these and layers React on top.

export type ToastType = "info" | "success" | "warning" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastInput {
  /** Optional override for auto-dismiss in milliseconds. 0 = persistent. */
  duration?: number;
  action?: ToastAction;
}

// Default durations in ms. Per shared-chrome-v2 §7: 5s for non-blocking
// types, 8s for elevated severity. Toasts with an action button get
// 8s unless the caller overrides (see computeToastDuration).
export const DEFAULT_DURATION: Record<ToastType, number> = {
  info: 5000,
  success: 5000,
  warning: 8000,
  error: 8000,
};

// Dwell time for cluster-merge "Merged N into X · Undo" toasts.
//
// Baker feedback (2026-06-27): working a long duplicates queue, each
// merge left a persistent Undo toast (action toasts default to
// duration=0) and they stacked up and never went away. He asked them to
// auto-dismiss after ~15-20s; 18s sits in that window. The merge stays
// reversible from the survivor's History tab after the toast is gone, so
// the shorter dwell costs no reversibility — it's an explicit override of
// the action-persistence default. Homed here (not in the component) so
// the invariant is unit-testable without importing the React tree.
export const MERGE_TOAST_DURATION_MS = 18_000;

// Default dwell for a toast that carries an action button (e.g. Undo).
export const ACTION_TOAST_DURATION_MS = 8_000;

// Cleanup UX rule 7 (CLAUDE.md): everything reversible has a 60-second
// undo. Marketing Hub audit 2026-10-27 (S1-H2, S3-M1): the 8s action
// default closed Today's Undo before anyone could reach it. The 8s default
// stays for action buttons that are not an Undo ("Open in Outlook").
export const UNDO_TOAST_DURATION_MS = 60_000;

// The longest a non-error toast may stay when a caller asks for "persistent"
// (duration: 0). Audit S3-M2: eight hub cards asked for toasts that never
// closed, and five of them piled over Connected accounts. A message the
// team must read stays up a full minute and is still dismissable; only an
// error may stay until someone closes it.
export const PERSISTENT_CAP_MS = 60_000;

// Most toasts on screen at once. Audit S3-M2: five stacked and covered the
// right column's controls. A sixth does not wait behind the others, the
// oldest one that is not offering an Undo simply goes.
export const MAX_VISIBLE_TOASTS = 3;

/** True for the "Undo" button on a toast; case-insensitive, "Undo all" counts too. */
export function isUndoAction(action: ToastAction | null | undefined): boolean {
  return !!action && /^undo/i.test(action.label.trim());
}

// Determine the effective duration for a toast given its type and the
// caller's options. Rules (in order of precedence):
//   1. An Undo action never lasts less than UNDO_TOAST_DURATION_MS, even
//      when the caller asked for less (the cluster-merge toast asked for
//      18s; the merge is also on the record's History tab, but the rule is
//      60 seconds on the toast too).
//   2. opts.duration is explicit → use it. 0 means persistent, which only
//      an error keeps; any other type is capped at PERSISTENT_CAP_MS.
//   3. any other action (no explicit duration) → ACTION_TOAST_DURATION_MS.
//      Andrea + Grant (2026-10-09): action toasts used to persist, so a run
//      of board drags stacked toasts over the screen until each was
//      closed by hand. A caller that truly needs one to stay passes 0.
//   4. fall back to DEFAULT_DURATION[type].
export function computeToastDuration(
  type: ToastType,
  opts?: ToastInput,
): number {
  const undo = isUndoAction(opts?.action);
  if (opts && typeof opts.duration === "number") {
    if (opts.duration === 0) {
      if (type === "error") return 0;
      return undo ? UNDO_TOAST_DURATION_MS : PERSISTENT_CAP_MS;
    }
    return undo ? Math.max(opts.duration, UNDO_TOAST_DURATION_MS) : opts.duration;
  }
  if (undo) return UNDO_TOAST_DURATION_MS;
  if (opts?.action) {
    return ACTION_TOAST_DURATION_MS;
  }
  return DEFAULT_DURATION[type];
}

// Which toast ids to drop so at most `max` remain. `toasts` is newest
// first (the provider's order). The oldest toast that offers no action goes
// first, because an Undo is the one thing a reader may still need; only if
// every toast offers an action does the oldest go.
export function toastIdsToDrop(
  toasts: ReadonlyArray<{ id: string; action: ToastAction | null }>,
  max: number = MAX_VISIBLE_TOASTS,
): string[] {
  const dropped: string[] = [];
  const remaining = [...toasts];
  while (remaining.length > max) {
    let idx = -1;
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      if (!remaining[i]!.action) {
        idx = i;
        break;
      }
    }
    if (idx === -1) idx = remaining.length - 1;
    dropped.push(remaining[idx]!.id);
    remaining.splice(idx, 1);
  }
  return dropped;
}

// Aria role for the toast surface. Errors + warnings raise visibility
// to assistive tech via role=alert; info + success use role=status so
// they're announced politely (or skipped on busy screens).
export function ariaRoleForToast(type: ToastType): "alert" | "status" {
  return type === "error" || type === "warning" ? "alert" : "status";
}

// Which live region the toast belongs in. Mirrors ariaRoleForToast
// but exposed separately because the live region is a wrapper-level
// concern (ToastContainer groups toasts into two streams) while the
// role is a per-item concern.
export function liveRegionForToast(type: ToastType): "polite" | "assertive" {
  return type === "error" || type === "warning" ? "assertive" : "polite";
}

// Generate a stable-enough toast id for a given runtime. Prefers
// crypto.randomUUID() (uniform across modern browsers + Node 20+);
// falls back to a millisecond+random combo for environments without
// it (jsdom < 21, very old browsers).
export function newToastId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}
